import { useCreateKey } from "@/api/useAuthKeys"
import { SourceIntegrationType } from "@/api/useAuthKeys.types"
import { getKodemCICommonExamples } from "@/api/useIntegrations.selector"
import {
  KodemCIPublicImageName,
  codeExample
} from "@/api/useIntegrations.types"
import { Button, Carousel, Flex, Modal, Steps, Text } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useLogger } from "@/hooks/useLogger"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { KeyIcon } from "@/icons"
import { CopyButton } from "@/molecules/CopyButton"
import { Protected } from "@/molecules/Protected"
import { HowToUseTemplate } from "@/pages/IntegrationsPage/components/templates/HowToUseTemplate"
import { CreateKeyModal } from "@/pages/SettingsPage/components/organisms/AuthKeysTab"
import { useState } from "react"
import { useNavigate } from "react-router"
import type { HowToUseProps, KodemCiIntegration } from "."
import { CodeItem } from "../../molecules/CodeItem"

import styles from "./HowToUseCIIntegration.module.scss"

export const HowToUseCIIntegration = (props: HowToUseProps) => {
  const { logger, EVENTS } = useLogger()

  const eventKey = props.name.toUpperCase() as KodemCiIntegration
  const { OPENED_GENERATE_KEY, COMMAND_GENERATE_KEY } =
    EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.KODEMCI[eventKey]

  const navigate = useNavigate()

  const [, modalContextHolder] = Modal.useModal()
  const [showModal, setShowModal] = useState<boolean | number>(false)
  const { t: tKodemCI } = useTranslation("translation", {
    keyPrefix: "integrations.connect.kodemCI"
  })

  const kodemCIVersion = props.extraInfo?.version || "latest"

  const { integrationSamplesArray, integrationSampleString } = Array.isArray(
    props.howToUse?.integrationSample
  )
    ? {
        integrationSamplesArray: props.howToUse
          ?.integrationSample as codeExample[],
        integrationSampleString: undefined
      }
    : {
        integrationSamplesArray: undefined,
        integrationSampleString: props.howToUse?.integrationSample as string
      }
  const selfHosted = isSelfHosted()

  const ciImageTag = selfHosted
    ? "kodem-image:tag"
    : `${KodemCIPublicImageName}:${kodemCIVersion}`
  const pullCommand = `docker pull ${ciImageTag}`
  const exportCommand = "export KODEM_SERVICE_KEY=secret"
  const { GOVERNANCE } = NAMES_ROUTES

  const kodemCiCommonExamples = getKodemCICommonExamples(ciImageTag, selfHosted)
  const status = "process" as const

  return (
    <>
      <HowToUseTemplate selfHosted={selfHosted} {...props}>
        <Steps
          size="small"
          theme="dark"
          orientation="vertical"
          items={[
            {
              title: tKodemCI("steps.pull.title"),
              content: (
                <div>
                  <Text type="secondary">
                    {tKodemCI("steps.pull.description")}
                  </Text>
                  <pre className="code-block-step-how-to-use-template-ks">
                    <code>{pullCommand}</code>
                    <CopyButton text={pullCommand} type="link" />
                  </pre>
                </div>
              ),
              status
            },
            {
              title: tKodemCI("steps.generateKey.title"),
              content: (
                <div className={styles.stepItem}>
                  <Flex vertical gap={4}>
                    <Text type="secondary">
                      {tKodemCI("steps.generateKey.description")}
                    </Text>
                    <Flex vertical gap={8}>
                      <div className="button-wrapper-step-how-to-use-template-ks">
                        <Protected
                          permission={{
                            resource: "settings.service-keys",
                            action: "write"
                          }}
                          shouldDisable
                        >
                          <Button
                            type="primary"
                            icon={<KeyIcon />}
                            onClick={() => {
                              logger.info(OPENED_GENERATE_KEY)
                              setShowModal(Date.now())
                            }}
                          >
                            {tKodemCI("steps.generateKey.buttonText")}
                          </Button>
                        </Protected>
                      </div>
                      <Text type="secondary">
                        {tKodemCI("steps.generateKey.note")}
                      </Text>
                    </Flex>
                  </Flex>
                  <pre className="code-block-step-how-to-use-template-ks">
                    <code>{exportCommand}</code>
                    <CopyButton text={exportCommand} type="link" />
                  </pre>
                </div>
              ),
              status
            },
            {
              title: tKodemCI("steps.prep_scan.title"),
              content: (
                <Flex vertical gap={8}>
                  <CodeItem
                    titleLine1={tKodemCI("steps.prep_scan.image_description")}
                    codeSnippet={tKodemCI("steps.prep_scan.image_pull")}
                  />
                  <CodeItem
                    titleLine1={tKodemCI("steps.prep_scan.repo_description")}
                    codeSnippet={tKodemCI("steps.prep_scan.repo_pull")}
                  />
                </Flex>
              ),
              status
            },
            {
              title: tKodemCI("steps.scan.title"),
              content: props.howToUse && (
                <div className="description-step-how-to-use-template-ks">
                  <Text type="secondary">
                    <Trans>
                      {tKodemCI("steps.scan.description", {
                        count: kodemCiCommonExamples.length
                      })}
                    </Trans>
                  </Text>
                  <Carousel arrows infinite={false}>
                    {kodemCiCommonExamples.map((sample, index) => (
                      <CodeItem
                        key={index}
                        {...sample} // titleLine1, titleLine2, infoLine, code
                        titleLine1={tKodemCI("steps.example", {
                          index: index + 1,
                          title: sample.titleLine1
                        })}
                        withCopy
                      />
                    ))}
                  </Carousel>
                </div>
              ),
              status
            },
            {
              title: tKodemCI(`steps.integrate.title.${props.name}`),
              content: props.howToUse?.integrationSample !== "TBD" && (
                <div>
                  <Text type="secondary">
                    {tKodemCI(`steps.integrate.description.${props.name}`)}
                  </Text>
                  {integrationSamplesArray && (
                    <Carousel arrows infinite={false}>
                      {integrationSamplesArray.map(
                        (sample: codeExample, index: number) => (
                          <CodeItem
                            key={index}
                            {...sample} // titleLine1, titleLine2, infoLine, codeSnippet
                            titleLine1={tKodemCI("steps.example", {
                              index: index + 1,
                              title: sample.titleLine1
                            })}
                            withCopy
                          />
                        )
                      )}
                    </Carousel>
                  )}
                  {integrationSampleString && (
                    <div className={styles.integrationSample}>
                      <div>
                        <pre className={styles.codeBlock}>
                          <code className={styles.scrollable}>
                            {props.howToUse?.integrationSample as string}
                          </code>
                        </pre>
                      </div>
                      <div>
                        <CopyButton
                          text={props.howToUse?.integrationSample as string}
                          type="link"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ),
              status
            },
            {
              title: tKodemCI("steps.ciPolicies.title"),
              content: (
                <Button
                  className={styles.noPadding}
                  type="link"
                  target="_blank"
                  onClick={() =>
                    navigate(`/${GOVERNANCE.ROOT}/${GOVERNANCE.CI_SCA}`)
                  }
                >
                  {tKodemCI("steps.ciPolicies.buttonText")}
                </Button>
              ),
              status
            }
          ]}
        />
      </HowToUseTemplate>
      <CreateKeyModal
        key={`${showModal}`}
        open={!!showModal}
        useCreateKey={useCreateKey}
        logCreateKey={(keyName) => {
          logger.info(COMMAND_GENERATE_KEY, { keyName })
        }}
        keyType={"SERVICE_KEYS"}
        onClose={() => {
          setShowModal(false)
        }}
        sourceIntegration={props.name as SourceIntegrationType}
      />
      {modalContextHolder}
    </>
  )
}
